interface UserDataModel {
    [key: string]: any; // TS TODO: Finalize interface
}

interface FlightOption {
    [key: string]: any; // TS TODO: Finalize interface
}

interface FailureReason {
    checkpointFailed: string; // e.g., "departureLocation", "dates", "times", "seats", "preferences"
    systemReason: string; // Log message explaining the failure
}

interface FlightOptionWithFailures extends FlightOption {
    failures: FailureReason[];
}

function isDateInRange(date: string, start: string, end: string): boolean {
    return date >= start && date <= end;
}

function doDateRangesOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
    return start1 <= end2 && start2 <= end1;
}

function isTimeInRange(time: string, start: string, end: string): boolean {
    return time >= start && time <= end;
}

function getTimeRangeOverlapMinutes(start1: string, end1: string, start2: string, end2: string): number {
    const toMinutes = (time: string) => {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        return hours * 60 + minutes + seconds / 60;
    };
    const startMinutes1 = toMinutes(start1);
    const endMinutes1 = toMinutes(end1);
    const startMinutes2 = toMinutes(start2);
    const endMinutes2 = toMinutes(end2);
    const overlapStart = Math.max(startMinutes1, startMinutes2);
    const overlapEnd = Math.min(endMinutes1, endMinutes2);
    return Math.max(0, overlapEnd - overlapStart);
}

export function findExactMatches(dataModel: UserDataModel, flightOptions: FlightOption[]): { exactMatches: FlightOption[]; otherOptions: FlightOptionWithFailures[] } {
    const exactMatches: FlightOption[] = [];
    const otherOptions: FlightOptionWithFailures[] = [];
  
    for (const option of flightOptions) {
      let isExactMatch = true;
      const failures: FailureReason[] = [];
  
      // Step 1: Check Departure Location
      if (option.departureLocationName !== dataModel.departureData.locationName) {
        isExactMatch = false;
        failures.push({
          checkpointFailed: "departureLocation",
          systemReason: `Departure location mismatch (user: ${dataModel.departureData.locationName}, option: ${option.departureLocationName})`,
        });
      }
  
      // Step 2: Check Arrival Location
      if (isExactMatch) {
        const preciseDestination = dataModel.arrivalData.preciseDestination?.locationName;
        if (preciseDestination) {
          if (option.arrivalLocationName !== preciseDestination) {
            isExactMatch = false;
            failures.push({
              checkpointFailed: "arrivalLocation",
              systemReason: `Arrival location mismatch (user: ${preciseDestination}, option: ${option.arrivalLocationName})`,
            });
          }
        } else {
          const generalType = dataModel.arrivalData.generalDestination?.destinationType;
          // Support legacy and current field names during transition.
          const generalState =
            dataModel.arrivalData.generalDestination?.state ??
            dataModel.arrivalData.generalDestination?.destinationState;
          const generalCountry =
            dataModel.arrivalData.generalDestination?.country ??
            dataModel.arrivalData.generalDestination?.destinationCountry;

          if (generalType && option.arrivalGeneralType !== generalType) {
            isExactMatch = false;
            failures.push({
              checkpointFailed: "arrivalLocation",
              systemReason: `Arrival general type mismatch (user: ${generalType}, option: ${option.arrivalGeneralType})`,
            });
          }
          if (generalState && option.arrivalGeneralState !== generalState) {
            isExactMatch = false;
            failures.push({
              checkpointFailed: "arrivalLocation",
              systemReason: `Arrival general state mismatch (user: ${generalState}, option: ${option.arrivalGeneralState})`,
            });
          }
          if (generalCountry && option.arrivalGeneralCountry !== generalCountry) {
            isExactMatch = false;
            failures.push({
              checkpointFailed: "arrivalLocation",
              systemReason: `Arrival general country mismatch (user: ${generalCountry}, option: ${option.arrivalGeneralCountry})`,
            });
          }
        }
      }
  
      // Step 3: Check Dates
      if (isExactMatch) {
        const userDateExact = dataModel.travelTiming.departureDateExact;
        const userDateRange = dataModel.travelTiming.departureDateRange;
        const optionDateExact = option.departureDateExact;
        const optionDateRangeStart = option.departureDateRangeStart;
        const optionDateRangeEnd = option.departureDateRangeEnd;
  
        let dateMatch = false;
        if (userDateExact) {
          if (optionDateExact) {
            dateMatch = optionDateExact === userDateExact;
          } else if (optionDateRangeStart && optionDateRangeEnd) {
            dateMatch = isDateInRange(userDateExact, optionDateRangeStart, optionDateRangeEnd);
          }
        } else if (userDateRange?.start && userDateRange?.end) {
          if (optionDateExact) {
            dateMatch = isDateInRange(optionDateExact, userDateRange.start, userDateRange.end);
          } else if (optionDateRangeStart && optionDateRangeEnd) {
            dateMatch = doDateRangesOverlap(
              userDateRange.start,
              userDateRange.end,
              optionDateRangeStart,
              optionDateRangeEnd
            );
          }
        } else {
          dateMatch = true;
        }
  
        if (!dateMatch) {
          isExactMatch = false;
          failures.push({
            checkpointFailed: "dates",
            systemReason: `Dates mismatch (userDateExact: ${userDateExact}, userDateRange: ${JSON.stringify(userDateRange)}, optionDateExact: ${optionDateExact}, optionDateRangeStart: ${optionDateRangeStart}, optionDateRangeEnd: ${optionDateRangeEnd})`,
          });
        }
      }
  
      // Step 4: Check Times
      if (isExactMatch) {
        const userTimeExact = dataModel.travelTiming.departureTimeExact;
        const userTimeRange = dataModel.travelTiming.departureTimeRange;
        const optionTimeExact = option.departureTimeExact;
        const optionTimeRangeStart = option.departureTimeRangeStart;
        const optionTimeRangeEnd = option.departureTimeRangeEnd;
  
        let timeMatch = false;
        if (userTimeExact) {
          if (optionTimeExact) {
            timeMatch = optionTimeExact === userTimeExact;
          } else if (optionTimeRangeStart && optionTimeRangeEnd) {
            timeMatch = isTimeInRange(userTimeExact, optionTimeRangeStart, optionTimeRangeEnd);
          }
        } else if (userTimeRange?.start && userTimeRange?.end) {
          if (optionTimeExact) {
            timeMatch = isTimeInRange(optionTimeExact, userTimeRange.start, userTimeRange.end);
          } else if (optionTimeRangeStart && optionTimeRangeEnd) {
            const overlapMinutes = getTimeRangeOverlapMinutes(
              userTimeRange.start,
              userTimeRange.end,
              optionTimeRangeStart,
              optionTimeRangeEnd
            );
            timeMatch = overlapMinutes > 0;
          }
        } else {
          timeMatch = true;
        }
  
        if (!timeMatch) {
          isExactMatch = false;
          failures.push({
            checkpointFailed: "times",
            systemReason: `Times mismatch (userTimeExact: ${userTimeExact}, userTimeRange: ${JSON.stringify(userTimeRange)}, optionTimeExact: ${optionTimeExact}, optionTimeRangeStart: ${optionTimeRangeStart}, optionTimeRangeEnd: ${optionTimeRangeEnd})`,
          });
        }
      }
  
      // Step 5: Check Seat Availability
      if (isExactMatch) {
        const userSeatsNeeded = dataModel.seatsNeeded;
        const userSeatsOffered = dataModel.seatsOffered;
        const userMaxSeats = dataModel.aircraftMaxSeats;
        const optionSeatsOffered = option.seatsOffered;
        const optionMaxSeats = option.aircraftMaxSeats;
        const optionSeatsNeeded = (optionSeatsOffered === null || optionSeatsOffered === undefined) ? 0 : undefined;
  
        let seatsMatch = false;
        if (userSeatsNeeded) {
          if (optionSeatsOffered !== null && optionSeatsOffered !== undefined) {
            seatsMatch = userSeatsNeeded <= optionSeatsOffered;
          } else if (optionSeatsNeeded !== undefined && userMaxSeats) {
            const totalSeatsNeeded = userSeatsNeeded + optionSeatsNeeded;
            seatsMatch = totalSeatsNeeded <= optionMaxSeats || totalSeatsNeeded <= userMaxSeats;
          } else {
            seatsMatch = userSeatsNeeded <= optionMaxSeats;
          }
        } else if (userSeatsOffered && userMaxSeats) {
          if (optionSeatsNeeded !== undefined) {
            seatsMatch = userSeatsOffered >= optionSeatsNeeded && (optionSeatsNeeded <= optionMaxSeats || optionSeatsNeeded <= userMaxSeats);
          } else if (optionSeatsOffered !== null && optionSeatsOffered !== undefined) {
            const totalSeatsOffered = userSeatsOffered + optionSeatsOffered;
            seatsMatch = totalSeatsOffered <= userMaxSeats && totalSeatsOffered <= optionMaxSeats;
          } else {
            seatsMatch = userSeatsOffered <= optionMaxSeats;
          }
        } else {
          seatsMatch = true;
        }
  
        if (!seatsMatch) {
          isExactMatch = false;
          failures.push({
            checkpointFailed: "seats",
            systemReason: `Seats mismatch (userSeatsNeeded: ${userSeatsNeeded}, userSeatsOffered: ${userSeatsOffered}, userMaxSeats: ${userMaxSeats}, optionSeatsOffered: ${optionSeatsOffered}, optionMaxSeats: ${optionMaxSeats})`,
          });
        }
      }
  
      // Step 6: Check Preferences
      if (isExactMatch) {
        let preferencesMatch = true;
  
        // Check 6.1: Pets Preference
        const userBringingPets = dataModel.isBringingPets ?? false;
        const userAllowsPets = dataModel.userData?.preferences?.isPetOk ?? dataModel.isPetOk ?? true;
        const optionBringingPets = option.isBringingPets ?? false;
        const optionAllowsPets = option.isPetOk;
        const petsMatch =
          (!userBringingPets || (userBringingPets && optionAllowsPets)) &&
          (!optionBringingPets || (optionBringingPets && userAllowsPets));
        if (!petsMatch) {
          preferencesMatch = false;
          failures.push({
            checkpointFailed: "preferences",
            systemReason: `Pets mismatch (userBringingPets: ${userBringingPets}, userAllowsPets: ${userAllowsPets}, optionBringingPets: ${optionBringingPets}, optionAllowsPets: ${optionAllowsPets})`,
          });
        }
  
        // Check 6.2: Children Preference
        if (preferencesMatch) {
          const userBringingChildren = dataModel.userData.preferences.isBringingChildren ?? false;
          const userAllowsChildren = dataModel.userData.preferences.isChildrenOk ?? true;
          const optionBringingChildren = option.isBringingChildren ?? false;
          const optionAllowsChildren = option.isChildrenOk;
          const childrenMatch =
            (!userBringingChildren || (userBringingChildren && optionAllowsChildren)) &&
            (!optionBringingChildren || (optionBringingChildren && userAllowsChildren));
          if (!childrenMatch) {
            preferencesMatch = false;
            failures.push({
              checkpointFailed: "preferences",
              systemReason: `Children mismatch (userBringingChildren: ${userBringingChildren}, userAllowsChildren: ${userAllowsChildren}, optionBringingChildren: ${optionBringingChildren}, optionAllowsChildren: ${optionAllowsChildren})`,
            });
          }
        }
  
        // Check 6.3: Quiet Flight Preference
        if (preferencesMatch) {
          const userRequiresQuietFlight = dataModel.userData.preferences.isQuietFlightOnly ?? false;
          const userMood = dataModel.reservationData?.expectedMood?.toLowerCase() ?? 'quiet';
          const optionRequiresQuietFlight = option.isQuietFlightOnly ?? false;
          const optionMood = option.expectedMood?.toLowerCase() ?? 'quiet';
          const quietFlightMatch =
            (!userRequiresQuietFlight || (userRequiresQuietFlight && optionMood === 'quiet')) &&
            (!optionRequiresQuietFlight || (optionRequiresQuietFlight && userMood === 'quiet'));
          if (!quietFlightMatch) {
            preferencesMatch = false;
            failures.push({
              checkpointFailed: "preferences",
              systemReason: `Quiet flight mismatch (userRequiresQuietFlight: ${userRequiresQuietFlight}, userMood: ${userMood}, optionRequiresQuietFlight: ${optionRequiresQuietFlight}, optionMood: ${optionMood})`,
            });
          }
        }
  
        // Check 6.4: Aircraft Preference
        if (preferencesMatch) {
          const userAircraft = dataModel.aircraft?.toLowerCase();
          const optionAircraft = option.aircraft?.toLowerCase();
          const aircraftMatch = !userAircraft || !optionAircraft || userAircraft === optionAircraft;
          if (!aircraftMatch) {
            preferencesMatch = false;
            failures.push({
              checkpointFailed: "preferences",
              systemReason: `Aircraft mismatch (userAircraft: ${userAircraft}, optionAircraft: ${optionAircraft})`,
            });
          }
        }
  
        if (!preferencesMatch) {
          isExactMatch = false;
        }
      }
  
      if (isExactMatch) {
        exactMatches.push(option);
      } else {
        otherOptions.push({ ...option, failures });
      }
    }
  
    return { exactMatches, otherOptions };
  }
